import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Switch,
  Button,
  Collapsible,
  useDisclosure,
  Field,
} from '@chakra-ui/react';
import { FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi'

const DAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
] as const

export interface DaySchedule {
  start: string
  end: string
}

export type WeekSchedule = Record<string, DaySchedule | null>

interface WorkScheduleEditorProps {
  value: WeekSchedule
  onChange: (schedule: WeekSchedule) => void
}

const DEFAULT_START = '09:00'
const DEFAULT_END = '17:00'

export function parseScheduleJson(json?: string | null): WeekSchedule {
  if (!json) return {}
  try {
    return JSON.parse(json) as WeekSchedule
  } catch {
    return {}
  }
}

export function scheduleToJson(schedule: WeekSchedule): string | undefined {
  const hasAnyDay = DAYS.some(d => schedule[d.key] != null)
  if (!hasAnyDay) return undefined
  return JSON.stringify(schedule)
}

export function WorkScheduleEditor({ value, onChange }: WorkScheduleEditorProps) {
  const { open, onToggle } = useDisclosure()
  const hasSchedule = DAYS.some(d => value[d.key] != null)

  const toggleDay = (dayKey: string, enabled: boolean) => {
    const next = { ...value }
    if (enabled) {
      next[dayKey] = { start: DEFAULT_START, end: DEFAULT_END }
    } else {
      next[dayKey] = null
    }
    onChange(next)
  }

  const updateTime = (dayKey: string, field: 'start' | 'end', time: string) => {
    const current = value[dayKey]
    if (!current) return
    onChange({
      ...value,
      [dayKey]: { ...current, [field]: time },
    })
  }

  const applyToAll = () => {
    // Find the first enabled day and copy its schedule to all days
    const firstEnabled = DAYS.find(d => value[d.key] != null)
    if (!firstEnabled || !value[firstEnabled.key]) return
    const template = value[firstEnabled.key]!
    const next: WeekSchedule = {}
    for (const day of DAYS) {
      if (value[day.key] != null) {
        next[day.key] = { ...template }
      } else {
        next[day.key] = null
      }
    }
    onChange(next)
  }

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="md" p={3}>
      <HStack
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        userSelect="none"
      >
        <HStack gap={2}>
          <Box as={FiClock} boxSize={4} color="gray.500" />
          <Text fontSize="sm" fontWeight="medium">
            Work Schedule
          </Text>
          {hasSchedule && (
            <Text fontSize="xs" color="gray.500">
              ({DAYS.filter(d => value[d.key] != null).length} days)
            </Text>
          )}
        </HStack>
        <Box as={open ? FiChevronUp : FiChevronDown} boxSize={4} color="gray.400" />
      </HStack>

      <Collapsible.Root open={open}>
        <Collapsible.Content>
          <VStack gap={2} mt={3} align="stretch">
            {DAYS.map(day => {
              const daySchedule = value[day.key]
              const isEnabled = daySchedule != null

              return (
                <HStack key={day.key} gap={3} align="center">
                  <Field.Root display="flex" alignItems="center" w="auto" minW="70px">
                    <Switch.Root
                      size="sm"
                      checked={isEnabled}
                      onCheckedChange={e => toggleDay(day.key, e.checked === true)}
                      colorPalette="brand"
                    >
                      <Switch.HiddenInput />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                    <Field.Label mb={0} ml={2} fontSize="xs" fontWeight="medium" minW="28px">
                      {day.label}
                    </Field.Label>
                  </Field.Root>
                  {isEnabled ? (
                    <HStack gap={1} flex={1}>
                      <Input
                        size="xs"
                        type="time"
                        value={daySchedule!.start}
                        onChange={e => updateTime(day.key, 'start', e.target.value)}
                        w="100px"
                      />
                      <Text fontSize="xs" color="gray.400">to</Text>
                      <Input
                        size="xs"
                        type="time"
                        value={daySchedule!.end}
                        onChange={e => updateTime(day.key, 'end', e.target.value)}
                        w="100px"
                      />
                    </HStack>
                  ) : (
                    <Text fontSize="xs" color="gray.400" fontStyle="italic">Off</Text>
                  )}
                </HStack>
              );
            })}

            {hasSchedule && (
              <Button
                size="xs"
                variant='link'
                colorPalette="brand"
                onClick={applyToAll}
                alignSelf="flex-start"
              >
                Apply first day's times to all enabled days
              </Button>
            )}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
